import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Backend work runs on the backend, not in this page, and SEVERAL runs can be in
// flight at once: every single-row "Research landlord" click starts its own operation
// and they run in parallel (see lead_research_api._kickoff_research), and the contacts
// pipeline advances a lead through four stages with nothing on this page driving it.
//
// So a page cannot track "the" run in one handle. It used to: launching a second
// single-row run overwrote the first's handle, which dropped the first's completion
// refresh entirely - its result never appeared in the panel, and its row kept a
// "researching" chip that nothing could ever clear.
//
// This tracks the whole SET instead, and takes the SERVER's own view of what is live
// (a row's `researchOperationKey`, a lead's pipeline `running`) as the truth rather
// than a page handle. It is deliberately kind-agnostic - one poller per page per kind
// of work, never a second copy of this logic per feature:
//   - while anything is live, `refresh` re-reads the rows on an interval, so a run
//     that finishes elsewhere (another row, another tab) clears its own chip,
//   - `markLaunched` covers only the gap between the click and the first re-read
//     that carries the new key,
//   - a ref the server reported live and no longer does fires `onFinished`, so the
//     page can pull that row's freshly-written research.

// A research run takes minutes, so for that caller the poll is about noticing it
// ENDED. The contacts pipeline also advances WITHIN a run (contacts pulled, emails
// drafted), so the same interval doubles as its progress cadence - which is why that
// caller points `refresh` at a small dedicated endpoint rather than the whole inbox.
const POLL_INTERVAL_MS = 8000;
// How long a just-launched row stays optimistically "researching" before its server
// key has appeared. Past this we stop asserting it: better to show the row as it
// actually reads than to leave a chip stuck on a run we can no longer see.
const LAUNCH_GRACE_MS = 45000;

export interface LiveRuns {
  // Rows launched from this page whose server key hasn't come back yet. The only
  // local signal left - everything else is read off the row.
  launchedRefs: ReadonlySet<string>;
  // Called with the row being launched, BEFORE the kickoff request, so the
  // optimistic state names that exact row - never "whatever is selected while some
  // kickoff is in flight", which mis-chips a row the operator merely clicked onto.
  markLaunched: (ref: string) => void;
  // The kickoff failed, so drop the optimism now rather than waiting out the grace.
  unmarkLaunched: (ref: string) => void;
}

export const useLiveRuns = ({
  liveRefs,
  refresh,
  onFinished,
  intervalMs = POLL_INTERVAL_MS,
}: {
  // Refs the SERVER currently reports work in flight for.
  liveRefs: ReadonlySet<string>;
  // Re-read the rows (invalidate the list / peek the search).
  refresh: () => void;
  // One ref's run has ended - pull anything keyed off that row ALONE. Omit it
  // where `refresh` already re-reads the whole row (the search page holds its
  // candidates in one list; the inbox has a separate per-lead read behind it).
  onFinished?: (ref: string) => void;
  intervalMs?: number;
}): LiveRuns => {
  // The callbacks and the live set are rebuilt every render, so the effects below
  // key on membership changing, not on identity - otherwise the poll would restart
  // on every render.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const liveRefsRef = useRef(liveRefs);
  liveRefsRef.current = liveRefs;
  const liveKey = useMemo(
    () => Array.from(liveRefs).sort().join(" "),
    [liveRefs]
  );

  const [launchedAtByRef, setLaunchedAtByRef] = useState<
    ReadonlyMap<string, number>
  >(() => new Map());
  const launchedAtRef = useRef(launchedAtByRef);
  launchedAtRef.current = launchedAtByRef;
  // Refs the server has confirmed as running, so "it finished" can be told apart
  // from "it was never live". Nothing renders from it, so it stays a ref.
  const confirmedRefs = useRef<Set<string>>(new Set());

  useEffect(() => {
    const live = liveRefsRef.current;
    const confirmed = confirmedRefs.current;
    const finished: string[] = [];
    confirmed.forEach((ref) => {
      if (!live.has(ref)) finished.push(ref);
    });
    finished.forEach((ref) => confirmed.delete(ref));
    live.forEach((ref) => confirmed.add(ref));
    // A launched run the server now reports is no longer optimistic.
    setLaunchedAtByRef((prev) => {
      if (prev.size === 0) return prev;
      const next = new Map(prev);
      live.forEach((ref) => next.delete(ref));
      return next.size === prev.size ? prev : next;
    });
    finished.forEach((ref) => onFinishedRef.current?.(ref));
  }, [liveKey]);

  const launchedKey = useMemo(
    () => Array.from(launchedAtByRef.keys()).sort().join(" "),
    [launchedAtByRef]
  );
  const active = liveKey.length > 0 || launchedKey.length > 0;
  useEffect(() => {
    if (!active) return;
    const tick = () => {
      const now = Date.now();
      const expired = Array.from(launchedAtRef.current.entries())
        .filter(([, at]) => now - at > LAUNCH_GRACE_MS)
        .map(([ref]) => ref);
      if (expired.length > 0) {
        setLaunchedAtByRef((prev) => {
          const next = new Map(prev);
          expired.forEach((ref) => next.delete(ref));
          return next;
        });
        // The run ended without us ever seeing its key (it finished inside the
        // grace window, or never started). Refresh that row once regardless, so a
        // result it did write still lands.
        expired
          .filter((ref) => !confirmedRefs.current.has(ref))
          .forEach((ref) => onFinishedRef.current?.(ref));
      }
      refreshRef.current();
    };
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [active, liveKey, launchedKey, intervalMs]);

  const markLaunched = useCallback((ref: string) => {
    setLaunchedAtByRef((prev) => {
      const next = new Map(prev);
      next.set(ref, Date.now());
      return next;
    });
    // The operation row exists before the kickoff responds, so this re-read
    // normally already carries the key - closing the optimistic window at once.
    refreshRef.current();
  }, []);

  const unmarkLaunched = useCallback((ref: string) => {
    setLaunchedAtByRef((prev) => {
      if (!prev.has(ref)) return prev;
      const next = new Map(prev);
      next.delete(ref);
      return next;
    });
  }, []);

  const launchedRefs = useMemo(
    () => new Set(launchedAtByRef.keys()),
    [launchedAtByRef]
  );

  return { launchedRefs, markLaunched, unmarkLaunched };
};
